import {
  ConnectNotificationBanner,
  ConnectPayments,
} from "@stripe/react-connect-js";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { PageWrapper } from "~/components/page-wrapper";
import { ConnectProvider } from "~/components/stripe/connect-provider";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

export const Route = createFileRoute("/_authenticated/payments")({
  component: PaymentsPage,
  pendingComponent: () => (
    <PageWrapper
      description="View your payment history and manage transactions"
      title="Payments"
    >
      {/* Skeleton loader matching the Stripe component structure */}
      <div className="bg-card animate-pulse rounded-xl border">
        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div className="bg-muted h-6 w-48 rounded-md" />
            <div className="bg-muted h-9 w-24 rounded-md" />
          </div>
          <div className="border-border flex gap-4 border-b pb-3">
            <div className="bg-muted h-4 w-24 rounded" />
            <div className="bg-muted h-4 w-32 rounded" />
            <div className="bg-muted h-4 w-20 rounded" />
            <div className="bg-muted h-4 flex-1 rounded" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div className="flex gap-4 py-3" key={i}>
              <div className="bg-muted h-4 w-24 rounded" />
              <div className="bg-muted h-4 w-32 rounded" />
              <div className="bg-muted h-4 w-20 rounded" />
              <div className="bg-muted h-4 flex-1 rounded" />
            </div>
          ))}
        </div>
      </div>
    </PageWrapper>
  ),
});

function PaymentsPage() {
  const { organization } = useActiveOrganization();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  if (!organization) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View your payment history and manage transactions"
        title="Payments"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View your payment history and manage transactions"
        title="Payments"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view payments
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="View your payment history and manage transactions"
        title="Payments"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view payment information. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="View your payment history, disputes, and issue refunds"
      title="Payments"
    >
      <ConnectProvider organizationId={organization.id}>
        <ConnectNotificationBanner />
      </ConnectProvider>
      <ConnectProvider
        className="bg-card overflow-hidden rounded-xl border p-6"
        organizationId={organization.id}
      >
        <ConnectPayments />
      </ConnectProvider>
    </PageWrapper>
  );
}
